import React from "react";
import { CreateAppVersionData } from "../../entry/server/app.ts";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

export const APP_OS_OPTIONS = [
  { value: "android", label: "اندروید" },
  { value: "ios", label: "iOS" },
  { value: "windows", label: "ویندوز" },
  { value: "macos", label: "macOS" },
  { value: "linux", label: "لینوکس" },
  { value: "android_tv", label: "اندروید تی‌وی" },
] as const;

export type AppVersionFormState = {
  os: string;
  version: string;
  version_name: string;
  link: string;
  is_force_update: boolean;
};

type Props = {
  form: AppVersionFormState;
  onChange: (field: keyof AppVersionFormState, value: string | boolean) => void;
};

export default function AppVersionFormFields({ form, onChange }: Props) {
  return (
    <>
      <label className="space-y-2">
        <span className="text-sm text-slate-300">سیستم‌عامل</span>
        <select
          className={inputClass}
          value={form.os}
          onChange={(e) => onChange("os", e.target.value)}
        >
          <option value="">انتخاب کنید</option>
          {APP_OS_OPTIONS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </label>

      <label className="space-y-2">
        <span className="text-sm text-slate-300">نسخه</span>
        <input
          type="number"
          min={1}
          value={form.version}
          onChange={(e) => onChange("version", e.target.value)}
          className={inputClass}
          placeholder="مثلاً ۱۰۰"
        />
      </label>

      <label className="space-y-2 md:col-span-2">
        <span className="text-sm text-slate-300">نام نسخه</span>
        <input
          value={form.version_name}
          onChange={(e) => onChange("version_name", e.target.value)}
          className={inputClass}
          placeholder="مثلاً نسخه ۱.۰.۰"
        />
      </label>

      <label className="space-y-2 md:col-span-2">
        <span className="text-sm text-slate-300">لینک دانلود</span>
        <input
          value={form.link}
          onChange={(e) => onChange("link", e.target.value)}
          className={inputClass}
          placeholder="https://..."
        />
      </label>

      <label className="flex items-center gap-2 md:col-span-2">
        <input
          type="checkbox"
          checked={form.is_force_update}
          onChange={(e) => onChange("is_force_update", e.target.checked)}
          className="h-4 w-4 accent-indigo-500"
        />
        <span className="text-sm text-slate-300">آپدیت اجباری</span>
      </label>
    </>
  );
}

export function emptyAppVersionForm(): AppVersionFormState {
  return {
    os: "",
    version: "",
    version_name: "",
    link: "",
    is_force_update: false,
  };
}

export function isAppVersionFormValid(form: AppVersionFormState) {
  const version = Number(form.version);
  const validOs = APP_OS_OPTIONS.some((item) => item.value === form.os);
  return Boolean(validOs && form.link.trim() && Number.isFinite(version) && version > 0 && form.version_name.trim());
}

export function toAppVersionPayload(form: AppVersionFormState): CreateAppVersionData {
  return {
    os: form.os as CreateAppVersionData["os"],
    version: Number(form.version),
    version_name: form.version_name.trim(),
    link: form.link.trim(),
    is_force_update: form.is_force_update,
  };
}

export function appOsLabel(os: string) {
  const found = APP_OS_OPTIONS.find((item) => item.value === os);
  return found?.label ?? os;
}
